import type { ComponentType, ReactNode } from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

interface EmptyStateProps {
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  title: string;
  /** Body text below the title. Alias: `description`. */
  subtitle?: string;
  description?: string;
  /** `{ label, onClick }` or a custom React node (e.g. a Button). */
  action?: EmptyStateAction | ReactNode;
  className?: string;
}

function isActionObject(action: EmptyStateAction | ReactNode): action is EmptyStateAction {
  return typeof action === 'object' && action !== null && 'label' in action && 'onClick' in action;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  subtitle,
  description,
  action,
  className,
}: EmptyStateProps) {
  const body = subtitle ?? description;
  const actionNode = action
    ? isActionObject(action)
      ? (
          <Button className="mt-6" onClick={action.onClick}>
            {action.label}
          </Button>
        )
      : <div className="mt-6">{action}</div>
    : null;

  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}>
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-surface/[0.04] ring-1 ring-surface/10">
        <Icon className="h-7 w-7 text-muted-foreground" />
      </div>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {body && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      )}
      {actionNode}
    </div>
  );
}
